import React, { useState, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  TextField,
  Button,
  Paper,
  Stack,
  Snackbar,
  Alert,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Email,
  Phone,
  LocationOn,
  AccessTime,
  Send as SendIcon,
} from '@mui/icons-material';

// Web3Forms configuration
const WEB3FORMS_ACCESS_KEY = process.env.REACT_APP_WEB3FORMS_ACCESS_KEY || '93bbd2ae-0145-4e58-b5ea-79d15a8faccc';
const WEB3FORMS_API_URL = 'https://api.web3forms.com/submit';

interface ContactFormData {
  name: string;
  email: string;
  phone: string;
  subject: string;
  message: string;
}

const Contact = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error'
  });
  const [formData, setFormData] = useState<ContactFormData>({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      subject: '',
      message: '',
    });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      // Create FormData for Web3Forms (requires FormData, not JSON)
      const formDataToSend = new FormData(e.currentTarget);
      
      // Add Web3Forms required fields - let Web3Forms use its default formatting
      formDataToSend.append('access_key', WEB3FORMS_ACCESS_KEY);
      formDataToSend.append('subject', `Contact Form: ${formData.subject}`);
      formDataToSend.append('from_name', 'Chishty UK Immigration Law Website');
      
      // Add honeypot field for spam protection (hidden field)
      formDataToSend.append('botcheck', '');
      
      // Send to Web3Forms API
      const response = await fetch(WEB3FORMS_API_URL, {
        method: 'POST',
        body: formDataToSend,
      });
      
      const data = await response.json();
      
      if (data.success) {
        console.log('Form submitted successfully:', data);
        setSnackbar({
          open: true,
          message: 'Your message has been sent successfully! We will contact you soon.',
          severity: 'success'
        });
        resetForm();
      } else {
        throw new Error(data.message || 'Failed to send message');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSnackbar({
        open: true,
        message: 'Failed to send your message. Please try again later.',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Box sx={{ py: { xs: 4, sm: 6, md: 8 } }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ mb: { xs: 4, sm: 6, md: 8 } }}>
          <Typography 
            variant={isMobile ? "h3" : "h1"} 
            align="center" 
            gutterBottom
            sx={{ fontWeight: 600 }}
          >
            Contact Us
          </Typography>
          <Typography 
            variant={isMobile ? "body1" : "h5"} 
            align="center" 
            color="textSecondary" 
            sx={{ maxWidth: '800px', mx: 'auto' }}
          >
            Get in touch with our immigration experts
          </Typography>
        </Box>

        <Grid container spacing={{ xs: 3, sm: 4, md: 6 }} direction={isTablet ? "column-reverse" : "row"}>
          {/* Contact Information */}
          <Grid item xs={12} md={4}>
            <Stack spacing={{ xs: 2, sm: 3, md: 4 }}>
              <Paper sx={{ p: { xs: 2, sm: 3 }, transition: 'transform 0.3s ease', '&:hover': { transform: 'translateY(-5px)' } }}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Email color="primary" sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }} />
                  <Box>
                    <Typography variant="h6">Email</Typography>
                    <Typography 
                      color="textSecondary" 
                      sx={{ 
                        wordBreak: 'break-word',
                        fontSize: { xs: '0.875rem', sm: '1rem' } 
                      }}
                    >
                      contact@chishtyukimmigrationlaw.info
                    </Typography>
                  </Box>
                </Stack>
              </Paper>

              <Paper sx={{ p: { xs: 2, sm: 3 }, transition: 'transform 0.3s ease', '&:hover': { transform: 'translateY(-5px)' } }}>
                <Stack direction="row" spacing={2} alignItems="flex-start">
                  <LocationOn color="primary" sx={{ fontSize: { xs: '1.5rem', sm: '2rem' }, mt: 0.5 }} />
                  <Box>
                    <Typography variant="h6">Office Locations</Typography>
                    <Typography 
                      color="textSecondary" 
                      sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                    >
                      1 Turner Road Atif Centre High Court Lahore
                    </Typography>
                  </Box>
                </Stack>
              </Paper>

              <Paper sx={{ p: { xs: 2, sm: 3 }, transition: 'transform 0.3s ease', '&:hover': { transform: 'translateY(-5px)' } }}>
                <Stack direction="row" spacing={2} alignItems="flex-start">
                  <AccessTime color="primary" sx={{ fontSize: { xs: '1.5rem', sm: '2rem' }, mt: 0.5 }} />
                  <Box>
                    <Typography variant="h6">Office Hours</Typography>
                    <Typography 
                      color="textSecondary" 
                      sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                    >
                      Monday – Friday: 9:00 AM – 5:30 PM
                    </Typography>
                    <Typography 
                      color="textSecondary" 
                      sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                    >
                      Saturday & Sunday: By Appointment Only
                    </Typography>
                  </Box>
                </Stack>
              </Paper>
            </Stack>
          </Grid>

          {/* Contact Form */}
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: { xs: 3, sm: 4 }, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
              <Typography variant={isMobile ? "h5" : "h4"} gutterBottom>
                Send Us a Message
              </Typography>
              <form onSubmit={handleSubmit}>
                {/* Honeypot field for spam protection (hidden) */}
                <input type="checkbox" name="botcheck" style={{ display: 'none' }} tabIndex={-1} autoComplete="off" />
                
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Your Name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      size={isMobile ? "small" : "medium"}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Email Address"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      size={isMobile ? "small" : "medium"}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Phone Number"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      size={isMobile ? "small" : "medium"}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      size={isMobile ? "small" : "medium"}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Message"
                      name="message"
                      multiline
                      rows={isMobile ? 3 : 4}
                      value={formData.message}
                      onChange={handleChange}
                      required
                      size={isMobile ? "small" : "medium"}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      size="large"
                      fullWidth
                      disabled={loading}
                      startIcon={<SendIcon />}
                      sx={{
                        py: { xs: 1.2, sm: 1.5 },
                        fontSize: { xs: '0.875rem', sm: '1rem' },
                        transition: 'all 0.3s',
                        '&:hover': {
                          transform: 'translateY(-3px)',
                          boxShadow: '0 6px 15px rgba(0,0,0,0.2)',
                        }
                      }}
                    >
                      {loading ? 'Sending...' : 'Send Message'}
                    </Button>
                  </Grid>
                </Grid>
              </form>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* Confirmation Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity} 
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Contact; 